<template>
  <div class="reports-view">
    <page-header title="Reports">
      <template #actions>
        <align small-gap>
          <icon-button
            icon="list"
            tooltip="Audit Log"
            @click="$router.push('/audit')"
          />

          <icon-button
            v-if="isAdmin"
            icon="api"
            tooltip="API Customers"
            @click="$router.push('/api-customers')"
          />
        </align>
      </template>
    </page-header>

    <section v-if="loaded">
      <div v-if="isAdmin" class="widget">
        <align class="header" flex>
          <h3 class="secondary-font">
            Active Members
          </h3>
        </align>

        <div class="grid">
          <stat-item
            size="xlarge"
            success
            label="Total"
            :value="$filters.number(report.totalMembers)"
          />

          <stat-item
            size="xlarge"
            success
            label="Web"
            :value="$filters.number(report.webMembers)"
          />

          <stat-item
            size="xlarge"
            success
            label="Mobile"
            :value="$filters.number(report.mobileMembers)"
          />

          <stat-item
            size="xlarge"
            success
            label="Employees"
            :value="$filters.number(report.employeeMembers)"
          />
        </div>
      </div>

      <div v-if="isAdmin" class="widget">
        <align class="header" flex>
          <h3 class="secondary-font">
            Paid Members
          </h3>
        </align>

        <div class="grid">
          <stat-item
            size="xlarge"
            success
            label="Total"
            :value="$filters.number(report.totalPaidMembers)"
          />

          <stat-item
            size="xlarge"
            success
            label="Web"
            :value="$filters.number(report.webPaidMembers)"
          />

          <stat-item
            size="xlarge"
            success
            label="Mobile"
            :value="$filters.number(report.mobilePaidMembers)"
          />

          <stat-item
            size="xlarge"
            success
            label="CC"
            :value="$filters.number(report.collectorsClubMembers)"
          />
        </div>
      </div>

      <div v-if="isAdmin" class="widget">
        <graph-overview
          v-if="dailyTotalPaidMembers"
          title="Daily Total Paid Members"
          y-axis-type="number"
          :zoom.sync="zoom"
          :scale.sync="scale"
          :stats="totalPaidNumbersStats"
          :graph-values="[dailyTotalPaidMembers]"
        />
      </div>

      <div v-if="isAdmin" class="widget">
        <align class="header" flex>
          <h3 class="secondary-font">
            Other Members
          </h3>
        </align>

        <div class="grid">
          <stat-item
            size="xlarge"
            success
            label="Total Free Trial"
            :value="$filters.number(report.totalFreeTrialers)"
          />

          <stat-item
            size="xlarge"
            success
            label="Web Free Trial"
            :value="$filters.number(report.webFreeTrialers)"
          />

          <stat-item
            size="xlarge"
            success
            label="Mobile Free Trial"
            :value="$filters.number(report.mobileFreeTrialers)"
          />

          <stat-item
            size="xlarge"
            success
            label="Free Members"
            :value="$filters.number(report.freeMembers)"
          />
        </div>
      </div>

      <div class="widget">
        <align class="header" flex>
          <h3 class="secondary-font">
            Verify Cards
          </h3>
        </align>

        <div class="grid">
          <stat-item
            size="xlarge"
            success
            label="Cards in Progress"
            :value="$filters.number(report.progressCount)"
          />

          <stat-item
            size="xlarge"
            success
            label="Cards in Review"
            :value="$filters.number(report.reviewCount)"
          />

          <stat-item
            size="xlarge"
            success
            label="Back to Progress"
            :value="$filters.number(report.monthlyMovedBackCount)"
          />
        </div>
      </div>

      <div class="widget">
        <align class="header" flex>
          <h3 class="secondary-font">
            Cards Added
          </h3>
        </align>

        <div class="grid">
          <stat-item
            size="xlarge"
            success
            label="Today"
            :value="$filters.number(dailyAddedCount)"
          />

          <stat-item
            size="xlarge"
            success
            label="Weekly"
            :value="$filters.number(report.weeklyAddedCount)"
          />

          <stat-item
            size="xlarge"
            success
            label="Monthly"
            :value="$filters.number(report.monthlyAddedCount)"
          />
        </div>
      </div>

      <div class="widget">
        <align class="header" flex>
          <h3 class="secondary-font">
            Profiles
          </h3>
        </align>

        <div class="grid">
          <stat-item
            size="xlarge"
            success
            label="Total Matched"
            :value="$filters.number(report.profilesMatchedManually + report.profilesMatchedAutomatically)"
          />

          <stat-item
            size="xlarge"
            success
            label="Manually Matched"
            :value="$filters.number(report.profilesMatchedManually)"
          />

          <stat-item
            size="xlarge"
            success
            label="Automatically Matched"
            :value="$filters.number(report.profilesMatchedAutomatically)"
          />
        </div>
      </div>

      <div class="employees">
        <h4 class="secondary-font">
          Employees
        </h4>

        <div class="employee-list-view">
          <align flex collapse-end>
            <div />

            <direction-sort-options
              :sort.sync="sort"
              :direction.sync="direction"
              :options="sortOptions"
            />
          </align>

          <div class="results">
            <template v-if="loaded">
              <div class="list">
                <employee-list-item
                  v-for="employee in employees"
                  :key="employee.uid"
                  :to="`/employee/${employee.uid}`"
                  :employee="employee"
                  :fields="['totalAddedCount', 'monthlyAddedCount', 'monthlyMovedBackCount', 'reviewCount']"
                  as="router-link"
                />
              </div>
            </template>

            <spinner v-else />
          </div>
        </div>
      </div>
    </section>

    <spinner v-else />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import * as Sentry from '@sentry/vue';
import firebase from 'firebase/compat/app';

import { indexService } from '@/services';

import { EMPLOYEE_SORT_OPTIONS } from '@cardladder/shared/constants';
import { getIndexData } from '@cardladder/shared/utils/index.util';
import StatItem from '@cardladder/shared/components/StatItem';
import DirectionSortOptions from '@cardladder/shared/components/DirectionSortOptions';
import GraphOverview from '@cardladder/shared/components/GraphOverview';

import EmployeeListItem from './EmployeeListItem';

export default {
  components: {
    StatItem,
    EmployeeListItem,
    DirectionSortOptions,
    GraphOverview,
  },

  data() {
    return {
      loaded: false,
      report: {},
      sort: 'monthlyAddedCount',
      direction: 'desc',
      dailyAddedCount: 0,
      zoom: 92,
      scale: 'linear',
      sortOptions: EMPLOYEE_SORT_OPTIONS,
    };
  },

  created() {
    this.load();
  },

  computed: {
    ...mapGetters({
      isAdmin: 'isAdmin',
    }),

    employees() {
      if (!this.report || !this.report.employees) {
        return [];
      }

      return Object.keys(this.report.employees)
        .map((employeeId) => this.report.employees[employeeId])
        .sort((a, b) => {
          if (this.direction === 'asc') {
            return a[this.sort] - b[this.sort];
          }

          return b[this.sort] - a[this.sort];
        });
    },

    paidMembersData() {
      if (!this.report || !this.report.dailyTotalPaidMembers) {
        return {};
      }

      return getIndexData({ dailyIndexTotal: this.report.dailyTotalPaidMembers }, this.zoom);
    },

    dailyTotalPaidMembers() {
      if (!this.paidMembersData || !this.paidMembersData.dailyIndexTotal) {
        return {};
      }

      return { label: 'Daily Total Paid Members', data: this.paidMembersData.dailyIndexTotal.data };
    },

    totalPaidNumbersStats() {
      if (!this.paidMembersData || !this.paidMembersData.dailyIndexTotal) {
        return [];
      }

      return this.paidMembersData.indexStats;
    },
  },

  methods: {
    async load() {
      this.loaded = false;

      try {
        this.report = await indexService.getReport();
        await this.getCardsAddedToday();
      } catch (e) {
        this.$toasted.error('Error loading report');
        Sentry.captureException(e);
      }

      this.loaded = true;
    },

    async getCardsAddedToday() {
      const startDate = moment().utcOffset('-06:00').startOf('day').toDate();
      const cardsAddedQuery = await firebase.firestore().collection('cards').where('dateAdded', '>=', startDate).get();

      let dailyAddedCount = 0;

      cardsAddedQuery.forEach((doc) => {
        if (doc.data() && !doc.data().hidden && !doc.data().status) {
          dailyAddedCount += 1;
        }
      });

      this.dailyAddedCount = dailyAddedCount;
    },
  },
};
</script>

<style lang="scss" scoped>
.reports-view {
  @include container;
  min-height: 100%;
}

.widget {
  margin-bottom: $gp * 2;

  .grid {
    display: grid;
    grid-gap: $gp;
    grid-template-columns: repeat(4, 1fr);

    @media($large-down) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .header {
    margin: 0 0 $gp;
  }

  h3 {
    margin: 0;
  }
}

.employees {
  margin-top: $gp;
}

.results {
  position: relative;
  flex: 1;
}

.list {
  margin-top: $gp;

  @media($medium-down) {
    margin: $gp (-$gp) 0;
  }
}
</style>
